export type Callback = (...args: any[]) => any;
export interface IEventSystem<E, T extends IEventHandlersMap = IEventHandlersMap> {
	context: any;
	events: IEvents;
	on<K extends keyof T>(name: K, callback: T[K], context?: any);
	detach(name: E, context?: any);
	clear(): void;
	fire<K extends keyof T>(name: K, args?: ArgumentTypes<T[K]>): boolean;
}

interface IEvent {
	callback: Callback;
	context: any;
}
interface IEvents {
	[key: string]: IEvent[];
}

interface IEventHandlersMap {
	[key: string]: Callback;
}
type ArgumentTypes<F extends (...args: any[]) => any> = F extends (...args: infer A) => any ? A : never;

export class EventSystem<E extends string, T extends IEventHandlersMap = IEventHandlersMap>
	implements IEventSystem<E, T> {
	public events: IEvents;
	public context: any;

	constructor(context?: any) {
		this.events = {};
		this.context = context || this;
	}
	on<K extends keyof T>(name: K, callback: T[K], context?: any) {
		const event: string = (name as string).toLowerCase();
		this.events[event] = this.events[event] || [];
		this.events[event].push({ callback, context: context || this.context });
	}
	detach(name: E, context?: any) {
		const event: string = name.toLowerCase();

		const eStack = this.events[event];
		if (context && eStack && eStack.length) {
			for (let i = eStack.length - 1; i >= 0; i--) {
				if (eStack[i].context === context) {
					eStack.splice(i, 1);
				}
			}
		} else {
			this.events[event] = [];
		}
	}
	fire<K extends keyof T>(name: K, args: ArgumentTypes<T[K]>): boolean {
		if (typeof args === "undefined") {
			args = [] as any;
		}

		const event: string = (name as string).toLowerCase();

		if (this.events[event]) {
			const res = this.events[event].map(e => e.callback.apply(e.context, args));
			return !res.includes(false);
		}
		return true;
	}
	clear() {
		this.events = {};
	}
}

export function EventsMixin(obj: any) {
	obj = obj || {};
	const eventSystem = new EventSystem(obj);
	obj.detachEvent = eventSystem.detach.bind(eventSystem);
	obj.attachEvent = eventSystem.on.bind(eventSystem);
	obj.callEvent = eventSystem.fire.bind(eventSystem);
}

export interface IEventFacade {
	attachEvent: any;
	callEvent: any;
}
